// @ts-nocheck
import { useEffect } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import {
  apiFetchInit,
  matchJobsApplyDest,
  getLatestAppForJob,
  fmtDate,
  LS_TOKEN,
  LS_ROLE,
  LS_CANDIDATE_ID,
  LS_HR_ID,
} from "@/legacy/helpersModule";
import { Login } from "@/features/auth/components/LoginForm";
import { ForgotPassword } from "@/features/auth/components/ForgotPassword";
import { ConsentScreen } from "@/features/auth/components/ConsentScreen";
import { CandReg } from "@/features/auth/components/CandRegForm";
import { CVUpload } from "@/features/apply/components/CVUpload";
import { useAppState } from "@/app/state/AppStateProvider";
import { Button, useToast } from "@/shared/components/ui";
import { SS_PENDING_JOB_APPLY } from "@/constants/storageKeys";

function loginPathWithEmail(email, returnTo) {
  const q = new URLSearchParams();
  if (email) q.set("email", email);
  if (returnTo) q.set("returnTo", returnTo);
  const suffix = q.toString();
  return "/login" + (suffix ? `?${suffix}` : "");
}

function readPendingGuestJobApply() {
  try {
    const raw = sessionStorage.getItem(SS_PENDING_JOB_APPLY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

export function LoginPage() {
  const {
    meta,
    navigate,
    setRole,
    setActiveId,
    setHrId,
    setSelJob,
    syncStateFromServer,
    candidateLoginPreferHomeRef,
    finalizeGuestJobApply,
  } = useAppState();
  const [searchParams] = useSearchParams();
  const emailFromUrl = searchParams.get("email") || "";

  // HR now signs in at /hr/login. Forward the links that predate the split —
  // old bookmarks, and the ?returnTo=/hr that ProtectedRoute used to generate —
  // so an HR user never lands on a candidate-only form with no way forward.
  const returnTo = searchParams.get("returnTo") || "";
  const wantsHr = searchParams.get("role") === "hr" || /^\/hr(\/|$|\?)/.test(returnTo);
  useEffect(() => {
    if (wantsHr) navigate("/hr/login" + (returnTo ? "?returnTo=" + encodeURIComponent(returnTo) : ""));
  }, [wantsHr, returnTo, navigate]);
  if (wantsHr) return null;

  return (
    <Login
      fixedRole="candidate"
      initialEmail={emailFromUrl}
      coolingMonths={meta?.coolingMonths}
      dpo={meta?.dpo}
      onCandSuccess={async ({ candidateId }) => {
        /* Session lives in the HttpOnly cookie; "cookie" is just a logged-in marker. */
        localStorage.setItem(LS_TOKEN, "cookie");
        localStorage.setItem(LS_ROLE, "candidate");
        localStorage.setItem(LS_CANDIDATE_ID, candidateId);
        setRole("candidate");
        setActiveId(candidateId);
        try {
          await syncStateFromServer();
        } catch (e) {}
        if (await finalizeGuestJobApply({ mode: "login" })) return;
        const preferHome = candidateLoginPreferHomeRef.current;
        candidateLoginPreferHomeRef.current = false;
        const sp = new URLSearchParams(window.location.search);
        const dest = preferHome ? "/" : sp.get("returnTo") || "/";
        const applyDest = matchJobsApplyDest(dest);
        if (applyDest) {
          const jr2 = await fetch("/api/jobs", apiFetchInit());
          if (jr2.ok) {
            const jd2 = await jr2.json();
            const jj = (jd2.jobs || []).find((x) => x.id === applyDest.jobId);
            if (jj) setSelJob(jj);
          }
        }
        navigate(dest);
      }}
      onHrSuccess={async ({ hrId }) => {
        localStorage.setItem(LS_TOKEN, "cookie");
        localStorage.setItem(LS_ROLE, "hr");
        localStorage.setItem(LS_HR_ID, hrId);
        setRole("hr");
        setHrId(hrId);
        try {
          await syncStateFromServer();
        } catch (e) {}
        const sp = new URLSearchParams(window.location.search);
        navigate(sp.get("returnTo") || "/hr");
      }}
      onRegister={() => {
        const sp = new URLSearchParams(window.location.search);
        const r = sp.get("returnTo");
        navigate("/register" + (r ? "?returnTo=" + encodeURIComponent(r) : ""));
      }}
      onForgot={() => navigate("/login/forgot")}
    />
  );
}

/**
 * HR sign-in, kept on its own route so the public candidate login never
 * advertises that an admin login exists. Shares the same <Login> component and
 * the same POST /api/auth/login call — only the audience is pinned.
 */
export function HrLoginPage() {
  const { meta, navigate, setRole, setHrId, syncStateFromServer } = useAppState();
  const [searchParams] = useSearchParams();

  return (
    <Login
      fixedRole="hr"
      coolingMonths={meta?.coolingMonths}
      dpo={meta?.dpo}
      onHrSuccess={async ({ hrId }) => {
        localStorage.setItem(LS_TOKEN, "cookie");
        localStorage.setItem(LS_ROLE, "hr");
        localStorage.setItem(LS_HR_ID, hrId);
        setRole("hr");
        setHrId(hrId);
        try {
          await syncStateFromServer();
        } catch (e) {}
        const rt = searchParams.get("returnTo") || "";
        // Only honour an in-ATS destination: a returnTo pointing at a candidate
        // page would drop an HR user somewhere they have no session for.
        navigate(/^\/hr(\/|$)/.test(rt) ? rt : "/hr");
      }}
    />
  );
}

export function ForgotPasswordPage() {
  const { navigate } = useAppState();
  return <ForgotPassword onBack={() => navigate("/login")} />;
}

export function RegisterPage() {
  const {
    meta,
    navigate,
    pending,
    setPending,
    registerPhase,
    setRegisterPhase,
    tpGuestEntryRef,
    tpGuestRegPrefill,
    setTpGuestRegPrefill,
    candidateLoginPreferHomeRef,
    setRole,
    setActiveId,
    setSelJob,
    setTalentPool,
    syncStateFromServer,
    finalizeGuestJobApply,
    clearGuestJobApply,
    guestJobApplyRef,
  } = useAppState();
  const toast = useToast();

  const goToLoginWithEmail = (email) => {
    const sp = new URLSearchParams(window.location.search);
    const r = sp.get("returnTo");
    navigate(loginPathWithEmail(email, r || undefined));
  };

  useEffect(() => {
    setRegisterPhase("consent");
  }, [setRegisterPhase]);

  if (registerPhase === "consent") {
    return (
      <ConsentScreen
        dataCategories={meta?.dataCategories}
        coolingMonths={meta?.coolingMonths}
        dpo={meta?.dpo}
        onAccept={(p) => {
          setPending(p);
          setRegisterPhase("form");
        }}
        onDecline={() => {
          if (tpGuestEntryRef.current) {
            navigate("/talent-pool");
            return;
          }
          const pendingApply =
            guestJobApplyRef.current || readPendingGuestJobApply();
          if (pendingApply?.jobId) {
            clearGuestJobApply();
            navigate("/jobs/" + pendingApply.jobId + "/apply");
            return;
          }
          const sp = new URLSearchParams(window.location.search);
          const r = sp.get("returnTo");
          navigate("/login" + (r ? "?returnTo=" + encodeURIComponent(r) : ""));
        }}
      />
    );
  }

  return (
    <CandReg
      key={tpGuestRegPrefill ? `tp-reg-${tpGuestRegPrefill.email}` : "reg"}
      initialName={tpGuestRegPrefill?.name || ""}
      initialEmail={tpGuestRegPrefill?.email || ""}
      onGoToLogin={goToLoginWithEmail}
      onRegister={async (f) => {
        if (!f.name.trim()) return;
        try {
          const res = await fetch(
            "/api/auth/register",
            apiFetchInit({
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: f.name,
              email: f.email,
              password: f.password,
              phone: f.phone,
              purposes: pending,
            }),
          }),
          );
          const data = await res.json().catch(() => ({}));
          if (!res.ok) {
            if (res.status === 409) {
              const err = new Error(data.error || "Account exists");
              err.code = "account_exists";
              err.email = f.email;
              throw err;
            }
            toast.show({
              tone: "danger",
              title: "Registration failed",
              message: data.error || "Registration failed",
              durationMs: 6000,
            });
            return;
          }
          localStorage.setItem(LS_TOKEN, "cookie");
          localStorage.setItem(LS_ROLE, "candidate");
          if (data.candidateId) localStorage.setItem(LS_CANDIDATE_ID, data.candidateId);
          setRole("candidate");
          setActiveId(data.candidateId);
          try {
            await syncStateFromServer();
          } catch (e) {}
          const tpPending = tpGuestEntryRef.current;
          if (tpPending && data.candidateId) {
            tpGuestEntryRef.current = null;
            setTpGuestRegPrefill(null);
            candidateLoginPreferHomeRef.current = false;
            const finalized = {
              ...tpPending,
              candidateId: data.candidateId,
              id: tpPending.id || "TP-" + Date.now(),
              submittedAsGuest: true,
            };
            try {
              const tpRes = await fetch(
                "/api/talent-pool",
                apiFetchInit({
                  method: "POST",
                  headers: { "Content-Type": "application/json" },
                  body: JSON.stringify(finalized),
                }),
              );
              if (!tpRes.ok) {
                const tpData = await tpRes.json().catch(() => ({}));
                toast.show({
                  tone: "danger",
                  title: "Talent pool failed",
                  message: tpData.error || "Could not submit to talent pool.",
                  durationMs: 6000,
                });
                navigate("/portal");
                return;
              }
            } catch (_) {
              toast.show({
                tone: "danger",
                title: "Network error",
                message: "Could not reach server.",
                durationMs: 6000,
              });
              navigate("/portal");
              return;
            }
            setTalentPool((p) => [...p, finalized]);
            navigate("/talent-pool/done");
            return;
          }
          if (await finalizeGuestJobApply({ mode: "register" })) return;
          const preferHome = candidateLoginPreferHomeRef.current;
          candidateLoginPreferHomeRef.current = false;
          const sp = new URLSearchParams(window.location.search);
          const dest = preferHome ? "/" : sp.get("returnTo") || "/";
          const applyDest = matchJobsApplyDest(dest);
          if (applyDest) {
            const jr2 = await fetch("/api/jobs", apiFetchInit());
            if (jr2.ok) {
              const jd2 = await jr2.json();
              const jj = (jd2.jobs || []).find((x) => x.id === applyDest.jobId);
              if (jj) setSelJob(jj);
            }
          }
          navigate(dest);
        } catch (e) {
          toast.show({
            tone: "danger",
            title: "Network error",
            message: "Could not reach server.",
            durationMs: 6000,
          });
        }
      }}
      onBack={() => {
        if (tpGuestEntryRef.current) {
          navigate("/talent-pool");
          return;
        }
        const pendingApply =
          guestJobApplyRef.current || readPendingGuestJobApply();
        if (pendingApply?.jobId) {
          navigate("/jobs/" + pendingApply.jobId + "/apply");
          return;
        }
        const sp = new URLSearchParams(window.location.search);
        navigate(loginPathWithEmail("", sp.get("returnTo") || undefined));
      }}
    />
  );
}

export function ApplyPage() {
  const { jobId } = useParams();
  const {
    jobs,
    selJob,
    setSelJob,
    maxCvMb,
    authCandidate,
    active,
    handleCVUploaded,
    handleGuestCVUploaded,
    navigate,
    storageReady,
  } = useAppState();

  useEffect(() => {
    if (!storageReady || !jobId) return;
    const j = jobs.find((x) => x.id === jobId);
    if (j) setSelJob(j);
  }, [storageReady, jobId, jobs, setSelJob]);

  const job = selJob || jobs.find((x) => x.id === jobId);
  if (!job) return null;

  const latestApp =
    authCandidate && active
      ? getLatestAppForJob(active.applicationHistory, job.id)
      : null;
  const inProgressApplication = latestApp && !latestApp.interviewCompletedAt;

  if (job.isOpen === false && !inProgressApplication) {
    return (
      <div className="min-h-dvh safe-bottom bg-surface-alt flex flex-col items-center justify-center px-4 safe-x">
        <div className="max-w-md w-full bg-white rounded-2xl border border-line p-6 sm:p-8 text-center">
          <p className="text-[10px] font-bold uppercase tracking-wide text-ink-500 mb-2">
            • Position closed
          </p>
          <h1 className="text-xl font-black text-ink-900 mb-2">{job.title}</h1>
          <p className="text-sm text-ink-500 mb-4">
            Applications are closed for this position
            {job.closedAt ? ` (closed ${fmtDate(job.closedAt)})` : ""}.
          </p>
          <Button type="button" variant="secondary" onClick={() => { navigate("/"); setSelJob(null); }}>
            Back to job board
          </Button>
        </div>
      </div>
    );
  }

  return (
    <CVUpload
      jobTitle={job.title}
      maxCvMb={maxCvMb}
      submitLabel={authCandidate ? undefined : "Continue to sign in →"}
      onComplete={authCandidate ? handleCVUploaded : handleGuestCVUploaded}
      onBack={() => {
        navigate("/");
        setSelJob(null);
      }}
    />
  );
}
